import React, { useState, useEffect } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Lock, Unlock, RotateCcw, SkipForward, Loader2 } from 'lucide-react';

/**
 * AdminCurriculumProgression — dialog for managing per-student curriculum progression:
 *   - toggle locked/flexible mode
 *   - reset progress (all topics back to pending)
 *   - skip a specific topic
 */
export const AdminCurriculumProgression = ({ open, onClose }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/curriculums');
      setRows(res.data?.curriculums || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const toggleMode = async (row) => {
    const next = row.progression_mode === 'locked' ? 'flexible' : 'locked';
    setSavingId(row.id);
    try {
      await api.put(`/admin/curriculum/${row.id}/progression`, { progression_mode: next });
      toast.success(`Set to ${next}`);
      setRows((r) => r.map((x) => (x.id === row.id ? { ...x, progression_mode: next } : x)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setSavingId(null);
    }
  };

  const resetProgress = async (row) => {
    if (!window.confirm(`Reset progress for ${row.student_name} (${row.module})?\nThis marks every topic back to pending.`)) return;
    setSavingId(row.id);
    try {
      const res = await api.post(`/admin/curriculums/${row.id}/reset-progress`);
      toast.success(`Reset — ${res.data?.topics_reset ?? 0} topics back to pending`);
      setRows((r) => r.map((x) => (x.id === row.id ? { ...x, progress_percent: 0, topics_completed: 0 } : x)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl" data-testid="admin-curriculum-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-indigo-600" /> Curriculum Progression Manager
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[65vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : rows.length === 0 ? (
            <p className="text-center text-slate-400 py-8 text-sm">No student curriculums assigned yet.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {rows.map((r) => (
                <div key={r.id} className="py-3 flex items-center gap-3" data-testid={`curr-row-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-extrabold text-[#0F172A] truncate">
                      {r.student_name} <span className="text-xs font-bold text-slate-400">· {r.citt_id}</span>
                    </p>
                    <p className="text-xs font-bold text-slate-500 truncate">
                      {r.vertical} · {r.module} · v{r.curriculum_version} · {r.progress_percent}% ({r.topics_completed}/{r.topics_total})
                    </p>
                  </div>
                  <button
                    onClick={() => toggleMode(r)}
                    disabled={savingId === r.id}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-extrabold ${
                      r.progression_mode === 'locked' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                    }`}
                    data-testid={`toggle-mode-${r.id}`}
                  >
                    {r.progression_mode === 'locked' ? <><Lock className="w-3 h-3" /> Locked</> : <><Unlock className="w-3 h-3" /> Flexible</>}
                  </button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => resetProgress(r)}
                    disabled={savingId === r.id || r.topics_completed === 0}
                    className="rounded-full text-xs"
                    data-testid={`reset-progress-${r.id}`}
                  >
                    <RotateCcw className="w-3 h-3 mr-1" /> Reset
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminCurriculumProgression;
